import { getCurrentFocusKey } from "@noriginmedia/norigin-spatial-navigation";
import {
  actionDialogClose,
  actionDialogOpen,
  dialogAtom,
} from "@sunrise/dialogs";
import { currentLanguageAtom } from "@sunrise/i18n";
import {
  datesRangeAtom,
  getDayOfWeekTranslation,
  nowAtom,
} from "@sunrise/time";
import { useTranslator } from "@sunrise/translator";
import { type Nullable } from "@sunrise/utils";
import { programIsPlayingAtTime } from "@sunrise/yallo-epg";
import { isSameDay } from "date-fns";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect, useState } from "react";

import {
  actionGuideJumpToDate,
  currentlyVisibleDateAtom,
  gridStateAtom,
  selectorGuideSelection,
} from "@/modules/guide/store";

/**
 * A hook depending on the dialogs module that will return the currently selected date + a functions to trigger a dialog to change the selected date.
 *
 * @returns
 */
export function useGridDateSelector(): {
  currentlySelectedDate: string;
  selectDate: () => Promise<void>;
  moveToNow: () => void;
  moveToPrimeTime: () => void;
} {
  const t = useTranslator();
  const language = useAtomValue(currentLanguageAtom);

  const currentSelectedDate = useAtomValue(currentlyVisibleDateAtom);
  const currentSelectedItem = useAtomValue(selectorGuideSelection);

  const [currentDate, setCurrentDate] = useState<string>("");

  const getNow = useAtomCallback(
    useCallback((get) => {
      return get(nowAtom);
    }, []),
  );

  useEffect(() => {
    const now = getNow();
    const getSelectedDate = async (): Promise<void> => {
      if (currentSelectedDate) {
        setCurrentDate(
          await getDayOfWeekTranslation(currentSelectedDate, now, language, t),
        );
      } else {
        setCurrentDate(await getDayOfWeekTranslation(now, now, language, t));
      }
    };

    void getSelectedDate();
  }, [currentSelectedDate, currentSelectedItem, getNow, language, t]);

  const selectDate = useAtomCallback(
    useCallback(
      async (get, set): Promise<void> => {
        const now = get(nowAtom);
        const datesRange = get(datesRangeAtom);

        const rangeOfAvailableDates = async (): Promise<
          { label: string; value: Date }[]
        > => {
          const promises = datesRange.map(async (date) => {
            return {
              label: await getDayOfWeekTranslation(date, now, language, t),
              value: date,
            };
          });

          return Promise.all(promises);
        };

        const range = await rangeOfAvailableDates();

        const handleSelectedDate = (value: Nullable<string>): void => {
          if (!value) return;
          set(gridStateAtom, actionGuideJumpToDate(new Date(value)));
          set(dialogAtom, actionDialogClose());
        };

        const activeRange = range.find((date) => {
          if (currentSelectedDate) {
            return isSameDay(currentSelectedDate, date.value);
          }

          if (currentSelectedItem) {
            return programIsPlayingAtTime(currentSelectedItem, date.value);
          }

          return false;
        });

        set(
          dialogAtom,
          actionDialogOpen({
            id: "grid-date-selector",
            type: "list",
            buttonTextAlign: "left",
            onButtonClick: handleSelectedDate,
            sections: [
              {
                options: range.map((date) => ({
                  label: date.label,
                  value: date.value.toUTCString(),
                })),
              },
            ],
            activeOptions: activeRange ? [activeRange.value.toUTCString()] : [],
            lastFocusKey: getCurrentFocusKey(),
          }),
        );
      },
      [language, t, currentSelectedDate, currentSelectedItem],
    ),
  );

  const moveToNow = useAtomCallback(
    useCallback((get, set) => {
      const now = get(nowAtom);
      set(gridStateAtom, actionGuideJumpToDate(now));
    }, []),
  );

  /**
   * Jumps to 20h on the currently active day.
   */
  const moveToPrimeTime = useAtomCallback(
    useCallback((get, set) => {
      const primeTime = new Date(get(currentlyVisibleDateAtom) ?? get(nowAtom));
      primeTime.setHours(20, 0, 0, 0);
      set(gridStateAtom, actionGuideJumpToDate(primeTime));
    }, []),
  );

  return {
    currentlySelectedDate: currentDate,
    selectDate,
    moveToNow,
    moveToPrimeTime,
  };
}
